import { useCallback, useMemo, useRef } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { FileListItem } from '@views/documents/elements/FileList'

import { MAX_FILE_SIZE_IN_BYTES } from '../../../constants/uploadConstants'
import { useDocumentsDragAndDrop } from '../../../contexts/DocumentsDragAndDropContext'
import { useUploadDocuments } from './useUploadDocuments'

interface UseFolderContentDragAndDropProps {
  folderId: string | undefined
}

export const useFolderContentDragAndDrop = (props: UseFolderContentDragAndDropProps) => {
  const { folderId } = props
  const { t } = useTranslation()
  const { draggedItem, moveDraggedItemToFolder, startDraggingItem, stopDraggingItem } = useDocumentsDragAndDrop()
  const { uploadDocuments, isUploading } = useUploadDocuments({ folderId })

  // Drops onto a folder row bubble up to the outer Dropzone's own onDrop (needed so react-dropzone
  // resets its internal isDragActive state). This flag lets handleFilesSelect recognize that bubbled
  // event and skip re-uploading the same files into the currently displayed folder.
  const folderRowDropHandledRef = useRef(false)

  const handleFilesDropOnFolder = useCallback(
    (targetFolderId: string, files: File[]) => {
      folderRowDropHandledRef.current = true

      if (files.length > 0) {
        const rejectedFiles: FileRejection[] = []
        const acceptedFiles: File[] = []

        for (const file of files) {
          if (file.size > MAX_FILE_SIZE_IN_BYTES) {
            rejectedFiles.push({
              file,
              errors: [{ code: 'file-too-large', message: `${t('dropzone.error.file_too_large')} 20MB` }],
            })
          } else {
            acceptedFiles.push(file)
          }
        }

        uploadDocuments(acceptedFiles, rejectedFiles.length > 0 ? rejectedFiles : undefined, targetFolderId)
      }
    },
    [t, uploadDocuments],
  )

  const handleFilesSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (folderRowDropHandledRef.current) {
        folderRowDropHandledRef.current = false
        return
      }

      if (acceptedFiles.length > 0 || rejectedFiles.length > 0) {
        uploadDocuments(acceptedFiles, rejectedFiles)
      }
    },
    [uploadDocuments],
  )

  const handleItemDragStart = useCallback(
    (item: FileListItem) => {
      if (!folderId) {
        return
      }
      startDraggingItem({ id: item.id, sourceFolderId: folderId, type: item.type })
    },
    [folderId, startDraggingItem],
  )

  const handleItemDrop = useCallback(
    (targetItem: FileListItem) => {
      moveDraggedItemToFolder(targetItem.id)
    },
    [moveDraggedItemToFolder],
  )

  return useMemo(
    () => ({
      draggedItem,
      handleFilesDropOnFolder,
      handleFilesSelect,
      handleItemDragEnd: stopDraggingItem,
      handleItemDragStart,
      handleItemDrop,
      isUploading,
    }),
    [
      draggedItem,
      handleFilesDropOnFolder,
      handleFilesSelect,
      handleItemDragStart,
      handleItemDrop,
      isUploading,
      stopDraggingItem,
    ],
  )
}
